import { Show, Filter, SearchInput, SelectInput } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Alert, Grid, Stepper, Step, StepLabel, Button, IconButton } from '@mui/material';
import { Warning, CheckCircle, Pending, HealthAndSafety, Security, Close } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { INCIDENT_STATUS, SEVERITY_COLORS, INCIDENT_SEVERITY } from './constants';
import { OHSWorkflowStatus } from './OHSWorkflowStatus';
import type { OHSIncidentRecord } from '@/types/entities';

const IncidentShowContent = ({ record }: { record: OHSIncidentRecord | undefined }) => {
  const navigate = useNavigate();
  if (!record) return null;
  const needsDOL = record.severity === 'Fatal' || record.severity === 'Major';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/workplace-injuries')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <PageDescription entity="OHS Incident" description="A recorded workplace injury incident. Triggers OHS reporting and tracking requirements." icon={<Warning />} />
      {needsDOL && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          Serious incident ({record.severity}) — ensure DOL reporting within 48 hours
        </Alert>
      )}
      {!needsDOL && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Standard incident — record keeping required
        </Alert>
      )}
      {record.status === 'Closed' && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Incident resolved — corrective actions implemented
        </Alert>
      )}

      <OHSWorkflowStatus
        entityId={record.id || ''}
        entityType="injury"
        currentState={record.status}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Reference Number', value: record.injuryNumber, bold: true },
          { label: 'Employee', value: record.employee?.id },
          { label: 'Incident Date', value: record.incidentDate },
          { label: 'Type', value: record.nature },
          { label: 'Severity', value: record.severity, chip: true },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Reported Date', value: record.reportedDate },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" sx={{ bgcolor: SEVERITY_COLORS[record.severity ?? ''], color: 'white' }} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>OHS Incident Process</Typography>
            <Stepper activeStep={INCIDENT_STATUS.indexOf(record.status || 'Open')} alternativeLabel>
              <Step completed={INCIDENT_STATUS.indexOf(record.status || 'Open') > 0}>
                <StepLabel>Reported</StepLabel>
              </Step>
              <Step completed={INCIDENT_STATUS.indexOf(record.status || 'Open') > 1}>
                <StepLabel>Investigating</StepLabel>
              </Step>
              <Step completed={INCIDENT_STATUS.indexOf(record.status || 'Open') > 2}>
                <StepLabel>Resolved</StepLabel>
              </Step>
              <Step completed={INCIDENT_STATUS.indexOf(record.status || 'Open') > 3}>
                <StepLabel>Closed</StepLabel>
              </Step>
            </Stepper>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>OHS Reporting Requirements</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { req: 'Fatality: 24 hours', met: record.severity === 'Fatal' },
                { req: 'Major injury: 48 hours', met: record.severity === 'Major' },
                { req: 'Near miss: Record only', met: record.severity === 'Minor' },
              ].map((r, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {r.met ? <CheckCircle color="success" fontSize="small" /> : <Pending color="warning" fontSize="small" />}
                  <Typography variant="body2">{r.req}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {record.description && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: SEVERITY_COLORS[record.severity ?? ''] }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Incident Description</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{record.description}</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button component="a" href="/ohs-certifications" variant="outlined" size="small" startIcon={<HealthAndSafety />}>
          View Certifications
        </Button>
        <Button component="a" href="/ohs-representatives" variant="outlined" size="small" startIcon={<Security />}>
          View Representatives
        </Button>
        <Button component="a" href="/employees" variant="outlined" size="small">
          View Employee
        </Button>
      </Box>
    </Box>
  );
};

export const IncidentShow = () => (
  <Show render={(props) => {
    const record = props.record as OHSIncidentRecord | undefined;
    return <IncidentShowContent record={record} />;
  }} />
);

export const IncidentFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search incident reference or employee" />
    <SelectInput source="severity" choices={INCIDENT_SEVERITY.map(s => ({ id: s, name: s }))} />
    <SelectInput source="status" choices={INCIDENT_STATUS.map(s => ({ id: s, name: s }))} />
    <SelectInput source="reportedToDol" choices={[{ id: 'true', name: 'Yes' }, { id: 'false', name: 'No' }]} label="DOL Reported" />
  </Filter>
);
